// Global Instructions Rule Applied! - 2026-05-08

import type { GlobalConfigRecord } from '../data_models/global_config_record.ts'
import type { MenuItemModel } from '../data_models/menu_item_model.ts'
import type { ReportingConfigModel } from '../data_models/reporting_config_model.ts'
import type { ReportingColumnDefaultsModel } from '../data_models/reporting_column_defaults_model.ts'
import type { ReportingColumnsModel } from '../data_models/reporting_columns_model.ts'
import {
  ViewConfigColumnSchema,
  type ViewConfigColumn
} from '../../core/components/ViewConfig/types.ts'
import { MENU_ITEM_KEY_DEALS, REPORT_CONFIG_NAME_DEALS } from '../globals.ts'

/**
 * Maps `cfg.reporting_column_defaults`: `field_value_ref` is the raw record key when
 * set; `field_name` is the display/label path. When `field_value_ref` is absent,
 * both resolve to `field_name`.
 */
export function reportingColumnRecordValueAndLabelKeys(
  col: Pick<ReportingColumnDefaultsModel, 'field_name' | 'field_value_ref'>
): { record_value_key: string; label_key: string | undefined } {
  const ref = col.field_value_ref
  const hasRef = typeof ref === 'string' && ref.length > 0
  return {
    record_value_key: hasRef ? ref : col.field_name,
    label_key: hasRef ? col.field_name : undefined
  }
}

/**
 * Single canonical converter from `(defaults, perReport?)` to {@link ViewConfigColumn}.
 *
 * `defaults` is the attribute row (`cfg.reporting_column_defaults`); `perReport` is
 * the optional per-report row (`cfg.reporting_columns`) carrying overrides plus
 * `display_order` / `visible` / `required`. NULL override = inherit attribute default.
 *
 * This is the SINGLE place per-usage overrides are reconciled. Adding a new
 * `*_override` to `cfg.reporting_columns` means extending {@link ReportingColumnsModel}
 * and applying it here — the TypeScript compiler then flags every consumer that
 * needs to handle the resulting field on {@link ViewConfigColumn}. The dev-time
 * `ViewConfigColumnSchema.parse` continues to fail loud if anything drops on the floor.
 */
export function reportingColumnToViewConfigColumn(
  defaults: ReportingColumnDefaultsModel,
  perReport?: ReportingColumnsModel | undefined,
  isDefaultColumn = false
): ViewConfigColumn {
  const { record_value_key, label_key } = reportingColumnRecordValueAndLabelKeys(defaults)

  const effective_column_title = perReport?.column_title_override ?? defaults.column_title
  const effective_min_width = perReport?.min_width_override ?? defaults.min_width
  const effective_freeze_column = perReport?.freeze_column_override ?? defaults.freeze_column
  const effective_filterable = perReport?.filterable_override ?? defaults.filterable
  const effective_editable = perReport?.editable_override ?? defaults.editable
  const effective_linkable = perReport?.linkable_override ?? defaults.linkable
  const effective_link_type = perReport?.link_type_override ?? defaults.link_type ?? null
  const effective_link_id_field =
    perReport?.link_id_field_override ?? defaults.link_id_field ?? null
  const effective_filter_type = perReport?.filter_type_override ?? defaults.filter_type ?? null
  const effective_filter_key = perReport?.filter_key_override ?? defaults.filter_key ?? null
  const effective_sort_key = perReport?.sort_key_override ?? defaults.sort_key ?? null
  const effective_required = perReport?.required ?? false

  // link_type / link_id_field are only meaningful when effective linkable=true
  // (mirrors the cfg.reporting_column_defaults_linkable_requires_link_type CHECK).
  const linkType = effective_linkable && effective_link_type ? effective_link_type : undefined
  const linkIdField = effective_linkable ? effective_link_id_field ?? undefined : undefined

  const out: ViewConfigColumn = {
    columnTitle: effective_column_title,
    valueKey: defaults.field_name,
    dataKey: record_value_key,
    fieldType: defaults.field_type,
    moduleKey: defaults.module_key,
    minWidth: effective_min_width,
    freezeColumn: effective_freeze_column,
    filterable: effective_filterable,
    sortable: Boolean(effective_sort_key),
    filterType: effective_filter_type ?? undefined,
    filterKey: effective_filter_key ?? undefined,
    sortKey: effective_sort_key ?? undefined,
    labelKey: label_key,
    tagDisplay: false,
    required: effective_required,
    editable: effective_editable,
    fieldKey: linkType,
    linkIdField,
    isDefaultColumn
  }
  if (process.env.NODE_ENV === 'development') {
    ViewConfigColumnSchema.parse(out)
  }
  return out
}

type DefaultsLookup = {
  byId: Map<string, ReportingColumnDefaultsModel>
  byFieldName: Map<string, ReportingColumnDefaultsModel>
}

function buildDefaultsLookup(
  reportingColumnDefaults: ReportingColumnDefaultsModel[] | undefined
): DefaultsLookup {
  const byId = new Map<string, ReportingColumnDefaultsModel>()
  const byFieldName = new Map<string, ReportingColumnDefaultsModel>()
  for (const d of reportingColumnDefaults ?? []) {
    byId.set(d.id, d)
    // (module_id, field_name) is UNIQUE in cfg.reporting_column_defaults but
    // distinct modules can share a field_name (cross-module reuse). For the
    // field_name-keyed lookup we rely on the report's linked-module filter to
    // narrow before we get here; first-write-wins is fine for the residual case.
    if (!byFieldName.has(d.field_name)) {
      byFieldName.set(d.field_name, d)
    }
  }
  return { byId, byFieldName }
}

function compareDisplayOrderNullsLast(
  a: ReportingColumnsModel,
  b: ReportingColumnsModel
): number {
  const aNull = a.display_order == null
  const bNull = b.display_order == null
  if (aNull && bNull) return 0
  if (aNull) return 1
  if (bNull) return -1
  return (a.display_order as number) - (b.display_order as number)
}

/**
 * Resolves default column order for a report by `reporting_config.id`.
 *
 * Per-report rows in `cfg.reporting_columns` carry `display_order` (NULLs sort
 * last) and `visible`. We layer per-report overrides onto the matching
 * attribute defaults via the converter.
 */
export function getReportingGridColumnsForReportId(
  reportId: string | null | undefined,
  globalConfigs: Pick<
    GlobalConfigRecord,
    'reporting_configs' | 'reporting_column_defaults' | 'reporting_columns'
  >
): ViewConfigColumn[] {
  if (!reportId) return []

  const report = globalConfigs.reporting_configs.find((r) => r.id === reportId)
  if (!report) return []

  const { byId } = buildDefaultsLookup(globalConfigs.reporting_column_defaults)

  const perReportRows = (globalConfigs.reporting_columns ?? [])
    .filter((c) => c.report_id === report.id)
    .slice()
    .sort(compareDisplayOrderNullsLast)

  const rows: ViewConfigColumn[] = []
  for (const pr of perReportRows) {
    if (pr.visible === false) continue
    const defaults = byId.get(pr.reporting_column_defaults_id)
    if (!defaults) continue
    rows.push(reportingColumnToViewConfigColumn(defaults, pr, true))
  }

  return rows
}

function getAllowedFieldNamesForReport(
  reportId: string,
  globalConfigs: Pick<GlobalConfigRecord, 'reporting_modules' | 'reporting_column_defaults'>
): Set<string> {
  const linkedModuleIds = new Set(
    (globalConfigs.reporting_modules ?? [])
      .filter((rm) => rm.report_id === reportId)
      .map((rm) => rm.module_id)
  )
  const allowed = new Set<string>()
  for (const d of globalConfigs.reporting_column_defaults ?? []) {
    if (linkedModuleIds.has(d.module_id)) {
      allowed.add(d.field_name)
    }
  }
  return allowed
}

/**
 * Builds grid columns using per-report `reporting_columns` order, or a user
 * preference list of `field_name` keys. User keys are restricted to columns
 * allowed for the report (`reporting_modules` ∩ `reporting_column_defaults`).
 *
 * For user-added columns not in the report's defaults, we synthesise a
 * ViewConfigColumn from the attribute row alone (no per-report overrides
 * exist). When the user's saved list re-orders existing default columns,
 * those keep their per-report overrides intact via the (report_id, field_name)
 * lookup.
 */
export function getReportingGridColumnsWithUserColumnKeys(
  reportId: string | null | undefined,
  globalConfigs: Pick<
    GlobalConfigRecord,
    'reporting_configs' | 'reporting_column_defaults' | 'reporting_columns' | 'reporting_modules'
  >,
  userColumnKeys: string[] | null
): ViewConfigColumn[] {
  if (!reportId) return []

  if (userColumnKeys === null) {
    return getReportingGridColumnsForReportId(reportId, globalConfigs)
  }

  const allowed = getAllowedFieldNamesForReport(reportId, globalConfigs)
  const { byId, byFieldName } = buildDefaultsLookup(globalConfigs.reporting_column_defaults)

  // Index per-report rows for this report by the attribute's field_name so the
  // user's `field_name`-keyed preference list can resolve back to the per-report
  // row that carries the overrides + visible + required.
  const perReportByFieldName = new Map<string, ReportingColumnsModel>()
  for (const pr of globalConfigs.reporting_columns ?? []) {
    if (pr.report_id !== reportId) continue
    const d = byId.get(pr.reporting_column_defaults_id)
    if (!d) continue
    perReportByFieldName.set(d.field_name, pr)
  }

  const defaultFieldNames = new Set(
    getReportingGridColumnsForReportId(reportId, globalConfigs).map((row) => row.valueKey)
  )

  const rows: ViewConfigColumn[] = []
  for (const k of userColumnKeys) {
    if (!allowed.has(k)) continue
    const pr = perReportByFieldName.get(k)
    // `pr.visible === false` means "not in the default display set"; the
    // user's preference list overrides that for their personal view.
    const defaults = pr ? byId.get(pr.reporting_column_defaults_id) : byFieldName.get(k)
    if (!defaults) continue
    rows.push(reportingColumnToViewConfigColumn(defaults, pr, defaultFieldNames.has(k)))
  }

  return rows
}

/**
 * Resolves `reporting_config.id` for a left-menu item (`menu_items.key`),
 * preferring a named report when provided.
 */
export function resolveReportIdForMenuItem(
  reportingConfigs: ReportingConfigModel[],
  menuItems: MenuItemModel[],
  menuItemKey: string,
  preferredReportName?: string | null
): string | undefined {
  // A menu key can legitimately appear in several categories (the Operations
  // section duplicates Deals / Funds / Prospects), so match reports against
  // ALL menu items carrying the key — first-match-by-key depends on the
  // delivered row order and broke every funds grid when the Operations copy
  // happened to sort first.
  const menuIds = new Set(menuItems.filter((m) => m.key === menuItemKey).map((m) => m.id))
  if (menuIds.size === 0) return undefined

  const forMenu = reportingConfigs.filter(
    (r) => r.menu_item_id != null && menuIds.has(r.menu_item_id)
  )
  if (preferredReportName) {
    const named = forMenu.find((r) => r.name === preferredReportName)
    if (named) return named.id
  }
  const markedDefault = forMenu.find((r) => r.default === true)
  if (markedDefault) return markedDefault.id

  return forMenu[0]?.id
}

/**
 * Resolves `reporting_config.id` for the Deals grid (menu item `deals`,
 * report name {@link REPORT_CONFIG_NAME_DEALS} preferred).
 */
export function resolveDealsReportId(
  reportingConfigs: ReportingConfigModel[],
  menuItems: MenuItemModel[]
): string | undefined {
  return resolveReportIdForMenuItem(reportingConfigs, menuItems, MENU_ITEM_KEY_DEALS, REPORT_CONFIG_NAME_DEALS)
}
